import {
  getWorkflowsStream,
  GraphDetails,
  Workflow,
  WorkflowStatus,
  type WorkflowStreamEvent,
} from "../../api";
import {
  $graphLayoutFormat,
  $initialGraphContent,
  $syncedGraphContent,
} from "../../stores/graph/content.ts";
import { $cytoscapeInstance } from "../../stores/graph/ui.ts";
import { $workflowsByGraphID } from "../../stores/workflows.ts";
import { applyLayoutFormat } from "../graph/apply-layout-format.ts";
import { getCurrentGraphId } from "../graph/get-current-graph-id.ts";
import { mergeGraphContent } from "../graph/merge-graph-content.ts";

const upsertWorkflowInStore = (workflow: Workflow): void => {
  // `updateWorkflowsStore()` only updates existing entries (it early returns if store empty),
  // but SSE may deliver workflows that aren't yet in the list (created elsewhere).
  const current = $workflowsByGraphID.get();
  const currentWorkflows = current.data || [];
  const existingIndex = currentWorkflows.findIndex((w) => w.id === workflow.id);

  if (existingIndex === -1) {
    $workflowsByGraphID.set({
      data: [workflow, ...currentWorkflows],
      error: current.error,
      loading: current.loading,
    });
    return;
  }

  const next = [...currentWorkflows];
  const existingWorkflow = next[existingIndex];
  // Update workflow properties from the new data, but preserve id and timestamp
  // (these are immutable identifiers that shouldn't change)
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    id: _id,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    timestamp: _timestamp,
    ...updatableWorkflowProps
  } = workflow;
  next[existingIndex] = {
    ...existingWorkflow,
    ...updatableWorkflowProps,
    id: existingWorkflow.id,
    timestamp: existingWorkflow.timestamp,
  };

  $workflowsByGraphID.set({
    data: next,
    error: current.error,
    loading: current.loading,
  });
};

// Note: We intentionally do not normalize timestamps (we don't care about them),
// and we rely on the server emitting `WorkflowStreamEvent` payloads.

const updateGraphWithWorkflows = async (
  _graphId: string,
  graphDetails: GraphDetails,
): Promise<void> => {
  const currentGraph = $syncedGraphContent.get();

  if (currentGraph) {
    const currentNodeIds = new Set(currentGraph.nodes.map((n) => n.id));
    const currentEdgeIds = new Set(currentGraph.edges.map((e) => e.id));

    const hasNewNodes = graphDetails.nodes.some(
      (node) => !currentNodeIds.has(node.id),
    );
    const hasNewEdges = graphDetails.edges.some(
      (edge) => !currentEdgeIds.has(edge.id),
    );

    const hasNewElements = hasNewNodes || hasNewEdges;
    const updatedGraph = mergeGraphContent(currentGraph, graphDetails);
    $syncedGraphContent.set(updatedGraph);

    if (hasNewElements) {
      const cytoscapeInstance = $cytoscapeInstance.get();
      const currentFormat = $graphLayoutFormat.get();
      if (cytoscapeInstance) {
        applyLayoutFormat(cytoscapeInstance, currentFormat);
      }
    }
  }
};

export const createWorkflowPolling = (): {
  getStatus: () => boolean;
  start: () => void;
  stop: () => void;
} => {
  let abortController: AbortController | null = null;
  let isRunning = false;

  const start = (): void => {
    if (isRunning) return;

    const graphId = getCurrentGraphId();
    if (graphId === null) return;

    abortController = new AbortController();
    isRunning = true;

    (async (): Promise<void> => {
      try {
        const sse = await getWorkflowsStream({
          headers: {
            Accept: "text/event-stream",
          },
          path: { graphId },
          signal: abortController?.signal,
        });

        for await (const event of sse.stream) {
          if (abortController?.signal.aborted) break;

          const workflowEvent =
            (event as unknown as { data?: WorkflowStreamEvent }).data ??
            (event as unknown as WorkflowStreamEvent);

          if (workflowEvent.type === "workflow:started") {
            upsertWorkflowInStore(workflowEvent.workflow);
            continue;
          }

          if (
            workflowEvent.type === "workflow:success" ||
            workflowEvent.type === "workflow:failed"
          ) {
            // Check if this is a net new completion (workflow wasn't already SUCCEEDED)
            const currentWorkflows = $workflowsByGraphID.get().data || [];
            const existingWorkflow = currentWorkflows.find(
              (w) => w.id === workflowEvent.workflow.id,
            );
            const isNetNewCompletion =
              workflowEvent.type === "workflow:success" &&
              (!existingWorkflow ||
                existingWorkflow.status !== WorkflowStatus.SUCCEEDED);

            upsertWorkflowInStore(workflowEvent.workflow);

            if (isNetNewCompletion) {
              // CSV uploads may create or modify nodes and edges across the entire graph,
              // so we invalidate and refetch the content store to trigger a full reload.
              // This ensures the layout gets applied properly, just like a manual reload.
              if (workflowEvent.workflow.sourceId === "csv-upload") {
                $initialGraphContent.invalidate();
                // Explicitly fetch to ensure the data is reloaded and UI updates
                void $initialGraphContent.fetch();
              } else if (workflowEvent.graphDetails) {
                await updateGraphWithWorkflows(
                  graphId,
                  workflowEvent.graphDetails,
                );
              }
            }
          }
        }
      } catch (error) {
        // Swallow errors when intentionally stopped
        if (!abortController?.signal.aborted) {
          console.warn("Workflow SSE stream error:", error);
        }
      } finally {
        isRunning = false;
        abortController = null;
      }
    })();
  };

  const stop = (): void => {
    if (!abortController) {
      isRunning = false;
      return;
    }
    abortController.abort();
    abortController = null;
    isRunning = false;
  };

  const getStatus = (): boolean => isRunning;

  return { getStatus, start, stop };
};
